import { motion } from "framer-motion";
import { useLang } from "../../context/LanguageContext";
import { heroData, images } from "../../data/content";
import { ArrowDown } from "lucide-react";

const ease = [0.22, 1, 0.36, 1];

const Hero = () => {
  const { isAr } = useLang();
  const data = isAr ? heroData.ar : heroData.en;

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      className="relative w-full h-[100dvh] min-h-[640px] overflow-hidden"
      data-testid="hero-section"
    >
      <div className="absolute inset-0">
        <motion.img
          initial={{ scale: 1.05 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, ease, delay: 2.4 }}
          src={images.hero}
          alt="Premium interior with BigBrand doors"
          className="h-full w-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background: [
              "linear-gradient(180deg, rgba(8,28,24,0.64) 0%, rgba(8,28,24,0.18) 42%, rgba(8,28,24,0.92) 100%)",
              isAr
                ? "linear-gradient(90deg, rgba(8,28,24,0.10) 0%, rgba(8,28,24,0.52) 58%, rgba(8,28,24,0.86) 100%)"
                : "linear-gradient(270deg, rgba(8,28,24,0.10) 0%, rgba(8,28,24,0.52) 58%, rgba(8,28,24,0.86) 100%)",
            ].join(", "),
          }}
        />
      </div>

      <div className="relative z-10 mx-auto flex h-full max-w-[1760px] flex-col justify-end px-6 pb-14 pt-28 md:px-10 md:pb-20 md:pt-32 lg:px-14 lg:pb-24">
        <div className={`max-w-[720px] ${isAr ? "ms-auto text-right" : "me-auto text-left"}`}>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.85, duration: 0.9, ease }}
            className={`${isAr ? "label-meta-ar" : "label-meta"} mb-4 max-w-xl text-bg-ivory/82 md:mb-5`}
          >
            {data.eyebrow}
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 36 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.95, duration: 1.2, ease }}
            className={`${isAr ? "arabic-title" : "serif-title"} max-w-[13ch] text-bg-ivory md:max-w-[15ch]`}
            style={{
              fontSize: isAr ? "clamp(38px, 5.15vw, 78px)" : "clamp(46px, 5.8vw, 86px)",
              lineHeight: isAr ? 1.14 : 1,
              fontWeight: isAr ? 600 : 500,
              letterSpacing: isAr ? "0" : "-0.025em",
              textShadow: "0 3px 28px rgba(8,28,24,0.62)",
            }}
          >
            {isAr ? (
              <>
                {data.title}{" "}
                <span className="text-champagne">{data.titleAccent}</span>
                <br />
                {data.titleEnd}
              </>
            ) : (
              <>
                {data.title}{" "}
                <em
                  style={{
                    fontStyle: "italic",
                    fontWeight: 400,
                    color: "rgba(232,211,195,1)",
                  }}
                >
                  {data.titleAccent}
                </em>
                <br />
                {data.titleEnd}
              </>
            )}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 3.15, duration: 1, ease }}
            className={`${isAr ? "arabic-title" : ""} mt-5 max-w-[560px] md:mt-6`}
            style={{
              color: "rgba(244,241,234,0.86)",
              fontSize: isAr ? 14.5 : 15.5,
              lineHeight: isAr ? 1.9 : 1.72,
              fontWeight: 300,
              textShadow: "0 2px 18px rgba(8,28,24,0.56)",
            }}
          >
            {data.desc}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 3.3, duration: 1, ease }}
            className={`mt-7 flex flex-wrap items-center gap-5 md:mt-9 md:gap-7 ${isAr ? "justify-end" : "justify-start"}`}
          >
            <button
              onClick={() => scrollTo("services")}
              className="bb-btn cursor-hover"
              style={{ background: "#F4F1EA", color: "#173F35" }}
              data-testid="hero-cta-explore"
            >
              {data.cta1}
              <span>→</span>
            </button>
            <button
              onClick={() => scrollTo("contact")}
              className="label-meta inline-flex items-center gap-3 border-b border-bg-ivory/45 pb-1.5 text-bg-ivory/88 transition-all duration-500 hover:gap-5 hover:text-bg-ivory cursor-hover"
              data-testid="hero-cta-contact"
            >
              {data.cta2}
              <span>→</span>
            </button>
          </motion.div>
        </div>
      </div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 3.45, duration: 1, ease }}
        onClick={() => scrollTo("about")}
        className={`absolute bottom-8 z-10 hidden items-center gap-2 label-meta text-bg-ivory/45 transition-colors hover:text-bg-ivory/85 md:flex ${
          isAr ? "left-6 md:left-10 lg:left-14" : "right-6 md:right-10 lg:right-14"
        } cursor-hover`}
        data-testid="hero-scroll"
      >
        {data.scroll}
        <ArrowDown size={13} strokeWidth={1.5} />
      </motion.button>
    </section>
  );
};

export default Hero;
